// eslint-env es6

import * as $ from 'jquery';
import * as ko from 'knockout';
import { find, map } from 'lodash';
import { keyCodes as keycodes } from '@pressreader/utils';
import { resolveValidationParameter } from './utils';

let autoId = 0;
const valueClass = 'select value';
const placeholderClass = 'select placeholder';

function toCollection(arr) {
    return map(arr, v => ({ value: v, text: v }));
}

class DropdownViewModel {
    constructor(
        {
            value,
            visible,
            options,
            optionsText,
            optionsValue,
            allowEmpty = true,
            validation,
            name,
            id,
            label,
            title,
            autocomplete = 'off',
            placeholder,
        },
        container,
    ) {
        if (options === undefined) {
            throw new Error('Required parameter "options" missing');
        }
        if (!ko.isObservable(value)) {
            throw new Error('"value" parameter must be ko.observable');
        }

        this.value = value;

        const autoGeneratedId = `dropdown${autoId++}`;
        this.name = name || autoGeneratedId;
        this.id = id || autoGeneratedId;

        this.visible = visible || true;
        this.placeholder = placeholder;
        this.label = label;
        this.title = title;
        this.autocomplete = autocomplete;
        this.allowEmpty = allowEmpty;

        if (optionsValue && optionsText) {
            // options is collection
            this.options = options;
            this.optionsValue = optionsValue;
            this.optionsText = optionsText;
        } else if (!optionsValue && !optionsText) {
            // options supposed to be plain array, where text and value are the same
            this.optionsValue = 'value';
            this.optionsText = 'text';
            this.options = Array.isArray(options) ? toCollection(options) : ko.pureComputed(() => toCollection(options()));
        } else if (optionsText) {
            // options supposed to be collection, where whole element is selected, not an its property.
            this.options = options;
            this.optionsValue = null;
            this.optionsText = optionsText;
        } else {
            throw new Error('dropdown misconfigured: parameter "optionsValue" must go together with "optionsText" which is empty');
        }

        // displayed text
        this.display = ko.observable('');
        this.displayClass = ko.observable('');

        this.hasFocus = ko.observable();
        this.validation = resolveValidationParameter(validation, value);

        // display initial value
        this._display(this.value());

        this._subscriptions = [];

        this._subscriptions.push(value.subscribe(val => this._display(val)));

        // update display when options arrived later than value
        if (ko.isObservable(this.options)) {
            this._subscriptions.push(this.options.subscribe(() => this._display(ko.utils.unwrapObservable(value))));
        }

        this._mount(container);
    }

    _getSelectedOptionText(selectedValue) {
        const option =
            this.optionsValue === null // option itself is a value
                ? selectedValue
                : // value can be a number
                  // eslint-disable-next-line eqeqeq
                  find(ko.utils.unwrapObservable(this.options), item => item[this.optionsValue] == selectedValue);
        return option === undefined ? '' : ko.utils.unwrapObservable(option[this.optionsText]);
    }

    _display(value) {
        // value isn't selected - show placeholder
        if (value === undefined || value === null || value === '') {
            this.displayClass(placeholderClass);
            this.display(ko.utils.unwrapObservable(this.placeholder));
        } else {
            this.display(this._getSelectedOptionText(value));
            this.displayClass(valueClass);
        }
    }

    _mount(container) {
        const $container = $(container);
        $container.addClass('selectbox').removeAttr('params');

        ko.applyBindingsToNode(container, {
            visible: this.visible,
            validationContainer: this.validation,
            css: {
                focus: this.hasFocus,
            },
        });

        if (this.label !== undefined) {
            $container.prepend('<label data-bind="text: label, { attr: { for: id } }"></label>');
        }

        ko.utils.domNodeDisposal.addDisposeCallback(container, () => this.dispose());
    }

    onkeydown(data, event) {
        if (event.which === keycodes.delete && ko.utils.unwrapObservable(this.allowEmpty)) {
            event.preventDefault();
            this.value(null);
            return false;
        }
        return true;
    }

    dispose() {
        this._subscriptions.forEach(s => s.dispose());
        this._subscriptions.length = 0;

        // clean references to external observables
        this.value = undefined;
        this.visible = undefined;
        this.placeholder = undefined;
        this.options = undefined;
        this.optionsText = undefined;
        this.optionsValue = undefined;
        this.validation = undefined;
    }
}

/**
 * Knockout dropdown component.
 *
 * @description Component configuration.
 * @param {Object} params Component configuration object.
 * @param {ko.observable} value - selected value binding.
 * @param {Array.<String>|Array.<Object>|ko.observableArray} options - dropdown options.
 * @param {String} [optionsValue] - name of option property that will be used as value.
 * @param {String} [optionsText] - name of option property that will be used as text.
 * @param {ko.observable|Boolean} [validation] - validation observable,
 *                                               validation error(s) on this observable will be shown in validation area.
 *                                               if "true" the "value" will be treated as validation source.
 * @param {String|ko.observable} [label] - text label to the input that should be shown within the box.
 * @param {String|ko.observable} [title] - hint (title attribute).
 * @param {Boolean|ko.observable} [visible = true] - dropdown visibility expression.
 * @param {Boolean|ko.observable} [allowEmpty = true] - allow empty value,
 *                                                      if true - supports value reset on 'del' key
 *                                                      if not - first option will be automatically selected.
 * @param {String|ko.observable} [placeholder = ''] - placeholder expression.
 * @param {String} [name] - name of input (select) element, will be auto-generated if not set.
 * @param {String} [id] - id of input (select) element, will be auto-generated if not set.
 * @param {Object} [event] - event handlers map.
 *
 * @example
 *
 * ``` HTML
 *  <dropdown params="name: 'Region',
 *      value: regionCode,
 *      visible: regions().length > 0,
 *      options: regions,
 *      optionsText: 'displayName',
 *      optionsValue: 'code',
 *      allowEmpty: false "/>
 */
const componentDefinition = {
    synchronous: true,
    viewModel: {
        createViewModel: (params, componentInfo) => new DropdownViewModel(params, componentInfo.element),
    },
    template: `<select 
        data-bind="value: value, 
                   options: options, 
                   optionsText: optionsText, 
                   optionsValue: optionsValue, 
                   valueAllowUnset: allowEmpty,
                   event: { keydown: onkeydown },
                   autocomplete: autocomplete,
                   hasFocus: hasFocus,
                   attr: { name: name, id: id, title: title }"></select><span data-bind="text: display, css: displayClass"></span>`,
};

const COMPONENT_NAME = 'dropdown';

/**
 * Component registration
 */
if (!ko.components.isRegistered(COMPONENT_NAME)) {
    ko.components.register(COMPONENT_NAME, componentDefinition);
}
